/**
 * Tiny API client. Uses fetch with credentials:include so the
 * curvle_at + curvle_rt + curvle_cid cookies travel automatically.
 *
 * On 401 with curvle_at expired, we transparently call /api/auth/refresh
 * once to get a new access token (the server rotates the refresh token
 * server-side). Subsequent fetches in the same call replay automatically.
 */
import {
  Outcome,
  OutcomeDetail,
  PublicPuzzleParams,
  Vec3,
} from "@/game";

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

const TIMEOUTS = {
  default: 15_000,
  auth: 8_000,
  attempt: 25_000,
};

function timeoutFor(path: string): number {
  if (path.startsWith("/api/auth/")) return TIMEOUTS.auth;
  if (path.startsWith("/api/attempt")) return TIMEOUTS.attempt;
  return TIMEOUTS.default;
}

export interface PuzzleResponse {
  puzzleId: string;
  seed: number;
  date: string;
  params: PublicPuzzleParams;
  stadiumName: string | null;
  conditionsLabel: string | null;
}

export interface AttemptRequest {
  puzzleId: string;
  input: { angle: number; power: number; curl: number };
}

export type KeeperReveal = {
  diveTo: { x: number; y: number; z: number };
  reactionTime: number;
  diveDuration: number;
  reach: number;
};

export interface AttemptResponse {
  attemptId: string;
  outcome: Outcome;
  outcomeDetail: OutcomeDetail | null;
  score: number;
  shareCardText: string;
  streak: { current: number; longest: number };
  trajectoryData: Vec3[];
  keeperReveal: KeeperReveal;
  /** Server set this attempt to the user's already-stored row for today.
   *  The outcome / trajectory belong to the user's ORIGINAL kick — the
   *  input the client just submitted is ignored. UI must not animate
   *  a fresh trajectory in this case. */
  alreadyAttempted?: boolean;
}

/**
 * Result of GET /api/attempt/today. Either the user has no attempt yet
 * (exists: false) or we return the canonical attempt payload so the client
 * can restore the post-kick UI on page load without re-POSTing.
 */
export type TodayAttemptResponse =
  | { exists: false; puzzleId: string }
  | {
      exists: true;
      attemptId: string;
      puzzleId: string;
      outcome: Outcome;
      outcomeDetail: OutcomeDetail | null;
      score: number;
      shareCardText: string;
      streak: { current: number; longest: number };
      trajectoryData: Vec3[];
      keeperReveal: KeeperReveal;
      input: { angle: number; power: number; curl: number };
    };

export type ProStatus = "none" | "active" | "past_due" | "canceled" | "expired";
export type PaymentProvider = "none" | "dodo" | "stripe" | "gumroad";

export interface AuthedUser {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  isPro: boolean;
  proStatus: ProStatus;
  /** ISO 8601 string; the date the user purchased Curvle Pro (lifetime
   *  unlock). Null if the user has never purchased. */
  proSince: string | null;
  /** Legacy column from the subscription era. For lifetime purchases
   *  this is null. Kept on the response for backward-compat with the
   *  Phase B-1 server contract; clients should not display it. */
  proExpiresAt: string | null;
  /** Legacy column from the subscription era. Always false for lifetime
   *  purchases. */
  cancelAtPeriodEnd: boolean;
  currentStreak: number;
  longestStreak: number;
  /** ISO 3166-1 alpha-2; "XX" if undetected/unset. Used by the country
   *  selector and country-locked Friend Leagues. */
  countryCode: string;
  /** Server-computed admin flag — true if User.isAdmin OR email is in
   *  ADMIN_EMAILS env. Drives visibility of the /admin link in UserPill. */
  isAdmin?: boolean;
  /** Founding-member number (1..N) if granted free lifetime Pro; null otherwise. */
  founderNumber: number | null;
}

export interface BillingStatus {
  isPro: boolean;
  proStatus: ProStatus;
  proSince: string | null;
  proExpiresAt: string | null;
  cancelAtPeriodEnd: boolean;
  paymentProvider: PaymentProvider;
  activePriceCents: number;
  activeTier: "wc_special" | "regular";
}

export interface MeResponse {
  authenticated: boolean;
  user: AuthedUser | null;
  expired?: boolean;
}

export interface LeaderboardRow {
  rank: number;
  userId: string;
  displayName: string;
  countryCode: string;
  score: number;
  outcome: Outcome;
  isPro: boolean;
  founderNumber: number | null;
  isMe: boolean;
}

export interface LeaderboardResponse {
  puzzleDate: string;
  totalPlayers: number;
  rows: LeaderboardRow[];
  me: LeaderboardRow | null;
  /** Page metadata — let the client lazy-load further pages and surface a
   *  Load-more button cleanly. `hasMore` is server-computed against the
   *  cached totalPlayers count so the UI can hide the button precisely. */
  offset: number;
  limit: number;
  hasMore: boolean;
  /** Echo of the ?country filter, uppercase ISO 3166-1 alpha-2 or null. */
  country: string | null;
}

export interface LeaderboardQuery {
  /** Default 0. */
  offset?: number;
  /** Default 100, max 500. */
  limit?: number;
  /** Optional ISO 3166-1 alpha-2; case-insensitive. */
  country?: string;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

let refreshInFlight: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const ctrl = new AbortController();
    const tid = window.setTimeout(() => ctrl.abort(), TIMEOUTS.auth);
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        signal: ctrl.signal,
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      window.clearTimeout(tid);
    }
  })();
  const result = await refreshInFlight;
  refreshInFlight = null;
  return result;
}

async function request<T>(path: string, init?: RequestInit, allowRetry = true): Promise<T> {
  const ctrl = new AbortController();
  const tid = window.setTimeout(() => ctrl.abort(), timeoutFor(path));
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
      credentials: "include",
      signal: ctrl.signal,
      ...init,
    });
  } catch (err) {
    window.clearTimeout(tid);
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(408, "timeout");
    }
    throw err;
  } finally {
    window.clearTimeout(tid);
  }

  if (res.status === 401 && allowRetry && !path.startsWith("/api/auth/")) {
    const refreshed = await attemptRefresh();
    if (refreshed) return request<T>(path, init, false);
  }
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* noop */ }
    throw new ApiError(res.status, res.statusText, body);
  }
  // 204 No Content — return undefined cast to T; callers that pass void
  // type expect this. JSON parsing on empty body would throw.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  todayPuzzle: (): Promise<PuzzleResponse> => request<PuzzleResponse>("/api/puzzle/today"),
  /** Has the current user already attempted today's puzzle? Called on
   *  Pitch mount so the canvas can lock immediately if so. Never throws on
   *  "no attempt" — that's a normal {exists:false} response. */
  todayAttempt: (): Promise<TodayAttemptResponse> =>
    request<TodayAttemptResponse>("/api/attempt/today"),
  submitAttempt: (payload: AttemptRequest): Promise<AttemptResponse> =>
    request<AttemptResponse>("/api/attempt", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  leaderboardToday: (q: LeaderboardQuery = {}): Promise<LeaderboardResponse> => {
    const params = new URLSearchParams();
    if (typeof q.offset === "number") params.set("offset", String(q.offset));
    if (typeof q.limit === "number") params.set("limit", String(q.limit));
    if (q.country) params.set("country", q.country);
    const qs = params.toString();
    return request<LeaderboardResponse>(
      qs ? `/api/leaderboard/today?${qs}` : "/api/leaderboard/today",
    );
  },

  // ---- Phase 2 auth ----
  me: (): Promise<MeResponse> => request<MeResponse>("/api/auth/me"),
  requestMagicLink: (email: string): Promise<{ ok: true }> =>
    request<{ ok: true }>("/api/auth/request-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  signOut: (): Promise<{ ok: true }> =>
    request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  // ---- A.5 country override ----
  updateCountry: (countryCode: string): Promise<{ ok: true; user: AuthedUser }> =>
    request<{ ok: true; user: AuthedUser }>("/api/user/me", {
      method: "PATCH",
      body: JSON.stringify({ countryCode }),
    }),

  // ---- A.6 account deletion ----
  deleteAccount: (): Promise<void> =>
    request<void>("/api/account", { method: "DELETE" }),

  // ---- Plan B-V2 challenge viewer (the voyeur loop) ----
  attemptReplay: (
    attemptId: string,
  ): Promise<{
    attemptId: string;
    friend: {
      displayName: string;
      countryCode: string;
      score: number;
      outcome: Outcome;
      outcomeDetail: string | null;
      shareCardText: string | null;
    };
    puzzle: {
      id: string;
      date: string;
      params: PublicPuzzleParams;
      stadiumName: string | null;
      conditionsLabel: string | null;
    };
    input: { angle: number; power: number; curl: number };
    trajectoryData: Vec3[] | null;
    isToday: boolean;
    percentile: number | null;
    totalPlayers: number;
  }> => request(`/api/attempt/replay/${encodeURIComponent(attemptId)}`),

  // ---- Billing / Pro (Gumroad — sole live MOR as of 2026-06-02) ----
  // Curvle Pro is a one-time lifetime unlock — no subscription cancel
  // endpoint exists. The 7-day refund flow is the only path back out.
  billingStatus: (): Promise<BillingStatus> =>
    request<BillingStatus>("/api/billing/status"),
  /** Public — remaining founding-member free-Pro slots. */
  founderStatus: (): Promise<{ remaining: number; limit: number }> =>
    request<{ remaining: number; limit: number }>("/api/founders/status"),
  startCheckout: (
    provider: "gumroad",
  ): Promise<{ checkoutUrl: string; provider: PaymentProvider; tier: string }> =>
    request("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ provider }),
    }),
  customerPortal: (): Promise<{ portalUrl: string }> =>
    request<{ portalUrl: string }>("/api/billing/portal"),
  /** Self-serve refund within the 7-day window. Returns 422 with
   *  `reason: "outside_window" | "prior_refund" | "provider_not_supported"`
   *  when ineligible for auto-refund — caller should show the email
   *  fallback UX. */
  requestRefund: (): Promise<{ ok: true; status: string; message: string }> =>
    request("/api/billing/refund", { method: "POST", body: JSON.stringify({}) }),

  // ---- Plan B-V4 Friend Leagues ----
  createLeague: (
    name: string,
    countryLock?: string,
  ): Promise<{ league: LeagueSummary & { joinUrl: string } }> =>
    request("/api/leagues", {
      method: "POST",
      body: JSON.stringify(countryLock ? { name, countryLock } : { name }),
    }),
  joinLeague: (
    code: string,
  ): Promise<{ league: LeagueSummary; alreadyMember?: boolean }> =>
    request("/api/leagues/join", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  myLeagues: (): Promise<{ leagues: Array<LeagueSummary & { joinedAt: string }> }> =>
    request("/api/leagues/me"),
  getLeague: (
    id: string,
  ): Promise<{ league: LeagueSummary; isMember: boolean }> =>
    request(`/api/leagues/${encodeURIComponent(id)}`),
  leagueLeaderboard: (
    id: string,
    date?: string,
  ): Promise<LeagueLeaderboardResponse> =>
    request(
      `/api/leagues/${encodeURIComponent(id)}/leaderboard${date ? `?date=${encodeURIComponent(date)}` : ""}`,
    ),
  leaveLeague: (id: string): Promise<void> =>
    request<void>(`/api/leagues/${encodeURIComponent(id)}/leave`, {
      method: "DELETE",
    }),
  deleteLeague: (id: string): Promise<void> =>
    request<void>(`/api/leagues/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // ---- Admin dashboard ----
  adminStats: (): Promise<AdminStats> => request<AdminStats>("/api/admin/stats"),
  adminUsers: (q: AdminUsersQuery = {}): Promise<AdminUsersResponse> => {
    const params = new URLSearchParams();
    if (typeof q.offset === "number") params.set("offset", String(q.offset));
    if (typeof q.limit === "number") params.set("limit", String(q.limit));
    if (q.sort) params.set("sort", q.sort);
    if (q.order) params.set("order", q.order);
    if (q.q) params.set("q", q.q);
    if (q.country) params.set("country", q.country);
    if (q.pro) params.set("pro", "1");
    const qs = params.toString();
    return request<AdminUsersResponse>(
      qs ? `/api/admin/users?${qs}` : "/api/admin/users",
    );
  },
};

// ---------------------------------------------------------------------------
// Friend Leagues types
// ---------------------------------------------------------------------------

export interface LeagueSummary {
  id: string;
  code: string;
  name: string;
  countryLock: string | null;
  maxMembers: number;
  memberCount: number;
  createdAt: string;
  createdById: string;
  myRole?: "creator" | "member";
}

export interface LeagueLeaderboardRow {
  rank: number | null;
  userId: string;
  displayName: string;
  countryCode: string;
  isPro: boolean;
  isMe: boolean;
  role: string;
  score: number;
  outcome: Outcome | null;
  hasPlayed: boolean;
  attemptId: string | null;
}

export interface LeagueLeaderboardResponse {
  leagueId: string;
  date: string;
  totalMembers: number;
  rows: LeagueLeaderboardRow[];
  me: LeagueLeaderboardRow | null;
}

// ---------------------------------------------------------------------------
// Admin dashboard types
// ---------------------------------------------------------------------------

export interface AdminStats {
  generatedAt: string;
  windowUtc: { today: string; last7dStart: string; last30dStart: string };
  users: {
    total: number;
    signedIn: number;
    signedInPct: number;
    newToday: number;
    new7d: number;
    new30d: number;
  };
  engagement: {
    dau: number;
    wau: number;
    mau: number;
    attemptsToday: number;
    attempts7d: number;
    attempts30d: number;
    attemptsTotal: number;
  };
  pro: {
    activeCount: number;
    newToday: number;
    new7d: number;
    conversionPct: number;
  };
  revenue: {
    totalCents7d: number;
    totalCents30d: number;
    estMrrCents: number;
    refundsCount7d: number;
    refundsCount30d: number;
    refundRate30dPct: number;
  };
  countries: {
    topByAttemptsToday: Array<{ code: string; count: number }>;
    topByUsersTotal: Array<{ code: string; count: number }>;
  };
  leagues: {
    total: number;
    members: number;
    createdToday: number;
  };
  webhooks: {
    recent: Array<{
      id: string;
      source: string;
      eventType: string;
      processedAt: string;
    }>;
  };
}

/**
 * Row shape from GET /api/admin/users. One row per signed-up User. Email
 * is null for anonymous-cookie-only users (rare to see here since the
 * default query has no `WHERE email IS NOT NULL` filter — they show up
 * when sorting by createdAt and have just an id + countryCode).
 */
export interface AdminUser {
  id: string;
  email: string | null;
  displayName: string | null;
  countryCode: string;
  isPro: boolean;
  isAdmin: boolean;
  currentStreak: number;
  longestStreak: number;
  /** ISO 8601 string; null if the user has never played. */
  lastPlayedAt: string | null;
  /** ISO 8601 string. */
  createdAt: string;
}

export interface AdminUsersQuery {
  /** Default 0. */
  offset?: number;
  /** Default 50, max 500. */
  limit?: number;
  /** Default "createdAt". */
  sort?: "createdAt" | "lastPlayedAt" | "currentStreak";
  /** Default "desc". */
  order?: "asc" | "desc";
  /** Email substring (case-insensitive), min 2 chars. */
  q?: string;
  /** ISO 3166-1 alpha-2, uppercase. Pass "XX" for unset. */
  country?: string;
  /** Filter to Pro accounts only. */
  pro?: boolean;
}

export interface AdminUsersResponse {
  rows: AdminUser[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}
